import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  Stack,
  Divider,
  Chip,
  Alert,
  AlertTitle,
  Button,
  LinearProgress,
  IconButton,
  Collapse,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  AccountBalance as AccountBalanceIcon,
  Savings as SavingsIcon,
  CalendarMonth as CalendarIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  OpenInNew as OpenInNewIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Receipt as ReceiptIcon,
  Description as InvoiceIcon,
  Calculate as CalculateIcon,
  Percent as PercentIcon,
  ArrowForward as ArrowForwardIcon,
  MoneyOff as MoneyOffIcon,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
} from "recharts";
import api from "../services/api";
import ExpenseBreakdown from "./ExpenseBreakdown";

// Irish tax constants for 2025
const CORP_TAX_RATE = 0.125; // 12.5% corporation tax
const CURRENT_YEAR = new Date().getFullYear();
const MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

// Format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat("en-IE", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);
};

// Quick Action Button Component
function QuickAction({ icon, label, onClick, color = "primary" }) {
  return (
    <Button
      variant="outlined"
      color={color}
      onClick={onClick}
      startIcon={icon}
      endIcon={<ArrowForwardIcon sx={{ fontSize: 16 }} />}
      sx={{
        justifyContent: "flex-start",
        py: 1.5,
        px: 2,
        borderRadius: 2,
        textTransform: "none",
        fontWeight: 500,
        "&:hover": {
          transform: "translateX(4px)",
          transition: "transform 0.2s",
        },
      }}
      fullWidth
    >
      {label}
    </Button>
  );
}

// Summary Card Component
function SummaryCard({ title, value, subtitle, icon, color = "primary" }) {
  return (
    <Card sx={{ height: "100%", borderRadius: 3 }}>
      <CardContent>
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Box>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              {title}
            </Typography>
            <Typography variant="h4" fontWeight={700} color={`${color}.main`}>
              {value}
            </Typography>
            {subtitle && (
              <Typography variant="caption" color="text.secondary">
                {subtitle}
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              p: 1,
              borderRadius: 2,
              bgcolor: `${color}.50`,
              color: `${color}.main`,
            }}
          >
            {icon}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
}

// Year-End Checklist Item
function ChecklistItem({ title, description, completed, deadline }) {
  return (
    <Box
      sx={{
        p: 2,
        borderRadius: 2,
        bgcolor: completed ? "success.50" : "grey.50",
        border: "1px solid",
        borderColor: completed ? "success.200" : "grey.200",
        mb: 1,
      }}
    >
      <Stack direction="row" spacing={2} alignItems="flex-start">
        {completed ? (
          <CheckCircleIcon color="success" />
        ) : (
          <WarningIcon color="warning" />
        )}
        <Box flex={1}>
          <Typography variant="subtitle2" fontWeight={600}>
            {title}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {description}
          </Typography>
          {deadline && (
            <Chip
              label={`Due: ${deadline}`}
              size="small"
              color={completed ? "success" : "warning"}
              sx={{ mt: 1 }}
            />
          )}
        </Box>
      </Stack>
    </Box>
  );
}

export default function DirectorDashboard() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [financialData, setFinancialData] = useState(null);
  const [showTaxGuide, setShowTaxGuide] = useState(false);
  const [error, setError] = useState(null);
  const [monthlyData, setMonthlyData] = useState([]);
  const [topExpenseCategories, setTopExpenseCategories] = useState([]);

  // Calculate year progress
  const today = new Date();
  const currentMonth = today.getMonth();
  const startOfYear = new Date(today.getFullYear(), 0, 1);
  const endOfYear = new Date(today.getFullYear(), 11, 31);
  const yearProgress = Math.round(
    ((today - startOfYear) / (endOfYear - startOfYear)) * 100
  );

  useEffect(() => {
    fetchFinancialData();
  }, []);

  const fetchFinancialData = async () => {
    setLoading(true);
    try {
      // Fetch income and expenses for the current year
      const startDate = `${CURRENT_YEAR}-01-01`;
      const endDate = `${CURRENT_YEAR}-12-31`;

      const [invoicesRes, receiptsRes] = await Promise.all([
        api.get(`/invoices?startDate=${startDate}&endDate=${endDate}`),
        api.get(`/receipts?startDate=${startDate}&endDate=${endDate}`),
      ]);

      const invoices = invoicesRes.data || [];
      const receipts = receiptsRes.data || [];

      // Initialize monthly buckets
      const monthBuckets = MONTHS.map((name, idx) => ({
        month: name,
        revenue: 0,
        expenses: 0,
      }));

      // Calculate totals and monthly breakdown
      let totalRevenue = 0;
      let outstandingAmount = 0;
      let outstandingCount = 0;

      invoices.forEach((inv) => {
        const amount =
          parseFloat(inv.total) || parseFloat(inv.totalAmount) || 0;
        const dateStr = inv.invoiceDate || inv.createdAt;
        const date = dateStr ? new Date(dateStr) : new Date();
        const monthIdx = date.getMonth();

        if (inv.status === "paid") {
          totalRevenue += amount;
          if (monthIdx >= 0 && monthIdx < 12) {
            monthBuckets[monthIdx].revenue += amount;
          }
        } else {
          outstandingAmount += amount;
          outstandingCount++;
        }
      });

      // Calculate expense totals and categories
      let totalExpenses = 0;
      const categoryTotals = {};

      receipts.forEach((rec) => {
        const amount = parseFloat(rec.amount) || parseFloat(rec.subtotal) || 0;
        const dateStr = rec.receiptDate || rec.createdAt;
        const date = dateStr ? new Date(dateStr) : new Date();
        const monthIdx = date.getMonth();
        const category = rec.category || "Other";

        totalExpenses += amount;
        if (monthIdx >= 0 && monthIdx < 12) {
          monthBuckets[monthIdx].expenses += amount;
        }

        categoryTotals[category] = (categoryTotals[category] || 0) + amount;
      });

      // Get top 5 expense categories
      const sortedCategories = Object.entries(categoryTotals)
        .map(([name, amount]) => ({ name, amount }))
        .sort((a, b) => b.amount - a.amount)
        .slice(0, 5);

      setTopExpenseCategories(sortedCategories);
      setMonthlyData(monthBuckets.slice(0, currentMonth + 1));

      const grossProfit = totalRevenue - totalExpenses;
      const estimatedCorpTax = Math.max(0, grossProfit * CORP_TAX_RATE);
      const retainedEarnings = grossProfit - estimatedCorpTax;

      // Calculate projected full-year based on current pace
      const monthsElapsed = currentMonth + 1;
      const projectedRevenue =
        monthsElapsed > 0 ? (totalRevenue / monthsElapsed) * 12 : 0;
      const projectedExpenses =
        monthsElapsed > 0 ? (totalExpenses / monthsElapsed) * 12 : 0;
      const projectedProfit = projectedRevenue - projectedExpenses;

      setFinancialData({
        totalRevenue,
        totalExpenses,
        grossProfit,
        estimatedCorpTax,
        retainedEarnings,
        invoiceCount: invoices.length,
        expenseCount: receipts.length,
        outstandingAmount,
        outstandingCount,
        projectedRevenue,
        projectedExpenses,
        projectedProfit,
      });
    } catch (err) {
      console.error("Error fetching financial data:", err);
      setError("Failed to load financial data");
    }
    setLoading(false);
  };

  // Year-end checklist items
  const checklistItems = [
    {
      title: "Review Outstanding Invoices",
      description:
        "Ensure all invoices are issued and chase any unpaid amounts before year-end",
      completed: false,
      deadline: "31 December",
    },
    {
      title: "Maximize Pension Contributions",
      description:
        "Company pension contributions are fully deductible. Consider maximizing before year-end.",
      completed: false,
      deadline: "31 December",
    },
    {
      title: "Small Benefit Exemption",
      description:
        "Directors can receive up to €1,000 tax-free in vouchers per year (2 vouchers max)",
      completed: false,
      deadline: "31 December",
    },
    {
      title: "Preliminary Corporation Tax",
      description:
        "Pay preliminary CT (90% of estimated liability) to avoid interest charges",
      completed: false,
      deadline: "23rd of month 6 after year-end",
    },
    {
      title: "File Annual Return (CRO)",
      description:
        "File your company's annual return with the Companies Registration Office",
      completed: false,
      deadline: "Within 56 days of ARD",
    },
  ];

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <LinearProgress />
        <Typography sx={{ mt: 2 }}>Loading financial summary...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Director Dashboard
        </Typography>
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body1" color="text.secondary">
            Year-end planning and tax optimization for {CURRENT_YEAR}
          </Typography>
          <Chip
            icon={<CalendarIcon />}
            label={`${yearProgress}% of year complete`}
            color="primary"
            variant="outlined"
          />
        </Stack>
      </Box>

      {/* Year Progress Bar */}
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3 }}>
        <Stack direction="row" justifyContent="space-between" mb={1}>
          <Typography variant="body2" color="text.secondary">
            Tax Year Progress
          </Typography>
          <Typography variant="body2" fontWeight={600}>
            {yearProgress}%
          </Typography>
        </Stack>
        <LinearProgress
          variant="determinate"
          value={yearProgress}
          sx={{ height: 10, borderRadius: 5 }}
        />
        <Stack direction="row" justifyContent="space-between" mt={1}>
          <Typography variant="caption" color="text.secondary">
            1 Jan {CURRENT_YEAR}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            31 Dec {CURRENT_YEAR}
          </Typography>
        </Stack>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Financial Summary Cards */}
      {financialData && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Total Revenue"
              value={formatCurrency(financialData.totalRevenue)}
              subtitle={`${financialData.invoiceCount} invoices`}
              icon={<TrendingUpIcon />}
              color="success"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Total Expenses"
              value={formatCurrency(financialData.totalExpenses)}
              subtitle={`${financialData.expenseCount} receipts`}
              icon={<AccountBalanceIcon />}
              color="error"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Gross Profit"
              value={formatCurrency(financialData.grossProfit)}
              subtitle="Before corporation tax"
              icon={<SavingsIcon />}
              color="primary"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <SummaryCard
              title="Est. Corporation Tax"
              value={formatCurrency(financialData.estimatedCorpTax)}
              subtitle="12.5% rate"
              icon={<AccountBalanceIcon />}
              color="warning"
            />
          </Grid>
        </Grid>
      )}

      {/* Outstanding Invoices Alert */}
      {financialData && financialData.outstandingCount > 0 && (
        <Alert
          severity="warning"
          icon={<MoneyOffIcon />}
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Button
              color="warning"
              size="small"
              onClick={() => navigate("/dashboard/invoices")}
            >
              View Invoices
            </Button>
          }
        >
          <AlertTitle>Outstanding Invoices</AlertTitle>
          You have {financialData.outstandingCount} unpaid invoice
          {financialData.outstandingCount > 1 ? "s" : ""} totaling{" "}
          <strong>{formatCurrency(financialData.outstandingAmount)}</strong>.
          Consider chasing payment before year-end.
        </Alert>
      )}

      {/* Quick Actions & Monthly Chart Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Quick Actions */}
        <Grid item xs={12} md={4}>
          <Paper sx={{ p: 3, borderRadius: 3, height: "100%" }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              ⚡ Quick Actions
            </Typography>
            <Stack spacing={1.5}>
              <QuickAction
                icon={<CalculateIcon />}
                label="Salary Optimizer"
                onClick={() => navigate("/dashboard/payroll")}
              />
              <QuickAction
                icon={<InvoiceIcon />}
                label="Create Invoice"
                onClick={() => navigate("/dashboard/invoices/new")}
              />
              <QuickAction
                icon={<ReceiptIcon />}
                label="Upload Receipt"
                onClick={() => navigate("/dashboard/upload")}
              />
              <QuickAction
                icon={<PercentIcon />}
                label="VAT Returns"
                onClick={() => navigate("/dashboard/vat")}
                color="secondary"
              />
              <QuickAction
                icon={<CheckCircleIcon />}
                label="Year-End Review"
                onClick={() => navigate("/dashboard/review")}
                color="success"
              />
            </Stack>
          </Paper>
        </Grid>

        {/* Monthly Revenue vs Expenses Chart */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, borderRadius: 3, height: "100%" }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              📊 Monthly Revenue vs Expenses
            </Typography>
            {monthlyData.length > 0 ? (
              <ResponsiveContainer width="100%" height={220}>
                <BarChart
                  data={monthlyData}
                  margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                >
                  <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
                  <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                  <YAxis
                    tick={{ fontSize: 12 }}
                    tickFormatter={(v) => `€${(v / 1000).toFixed(0)}k`}
                  />
                  <RechartsTooltip
                    formatter={(value) => formatCurrency(value)}
                    contentStyle={{
                      borderRadius: 8,
                      border: "none",
                      boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                    }}
                  />
                  <Bar
                    dataKey="revenue"
                    name="Revenue"
                    fill="#22c55e"
                    radius={[4, 4, 0, 0]}
                  />
                  <Bar
                    dataKey="expenses"
                    name="Expenses"
                    fill="#ef4444"
                    radius={[4, 4, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <Box
                sx={{
                  height: 220,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography color="text.secondary">
                  No data available yet
                </Typography>
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Projections & Top Expenses Row */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        {/* Full Year Projection */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              🔮 Full Year Projection
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Based on your current pace ({MONTHS[currentMonth]} YTD)
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Projected Revenue
                </Typography>
                <Typography variant="h6" fontWeight={600} color="success.main">
                  {financialData
                    ? formatCurrency(financialData.projectedRevenue)
                    : "—"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Projected Expenses
                </Typography>
                <Typography variant="h6" fontWeight={600} color="error.main">
                  {financialData
                    ? formatCurrency(financialData.projectedExpenses)
                    : "—"}
                </Typography>
              </Grid>
              <Grid item xs={4}>
                <Typography variant="caption" color="text.secondary">
                  Projected Profit
                </Typography>
                <Typography
                  variant="h6"
                  fontWeight={600}
                  color={
                    financialData?.projectedProfit >= 0
                      ? "primary.main"
                      : "error.main"
                  }
                >
                  {financialData
                    ? formatCurrency(financialData.projectedProfit)
                    : "—"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Top Expense Categories */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3 }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              💸 Top Expense Categories
            </Typography>
            {topExpenseCategories.length > 0 ? (
              <Stack spacing={1}>
                {topExpenseCategories.map((cat, idx) => (
                  <Box key={cat.name}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      mb={0.5}
                    >
                      <Typography variant="body2">{cat.name}</Typography>
                      <Typography variant="body2" fontWeight={600}>
                        {formatCurrency(cat.amount)}
                      </Typography>
                    </Stack>
                    <LinearProgress
                      variant="determinate"
                      value={
                        (cat.amount / topExpenseCategories[0].amount) * 100
                      }
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: "grey.100",
                        "& .MuiLinearProgress-bar": {
                          bgcolor:
                            idx === 0
                              ? "error.main"
                              : idx === 1
                              ? "warning.main"
                              : "grey.400",
                        },
                      }}
                    />
                  </Box>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">
                No expense data available
              </Typography>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Full Expense Breakdown with Filters */}
      <Box sx={{ mb: 3 }}>
        <ExpenseBreakdown year={CURRENT_YEAR} />
      </Box>

      {/* Tax Optimization Guide */}
      <Paper
        sx={{
          p: 0,
          mb: 3,
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: "primary.light",
          background: "linear-gradient(135deg, #eff6ff 0%, #f0fdf4 100%)",
        }}
      >
        <Box
          sx={{
            p: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
            "&:hover": { bgcolor: "rgba(0,0,0,0.02)" },
          }}
          onClick={() => setShowTaxGuide(!showTaxGuide)}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <InfoIcon color="primary" />
            <Typography variant="subtitle1" fontWeight={600}>
              Tax Optimization Strategies for Irish Directors
            </Typography>
          </Stack>
          <IconButton size="small">
            {showTaxGuide ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
        <Collapse in={showTaxGuide}>
          <Divider />
          <Box sx={{ p: 3 }}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  color="primary"
                >
                  💰 Salary vs Dividends
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Salary up to €42,000 taxed at 20% (standard rate)
                  <br />
                  • Above €42,000 taxed at 40% (higher rate)
                  <br />
                  • Dividends subject to income tax + 3% USC surcharge
                  <br />• Use the Salary Optimizer in Payroll section
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  color="primary"
                >
                  🏦 Pension Contributions
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Company contributions are fully deductible
                  <br />
                  • No BIK on employer contributions
                  <br />
                  • Age-based limits for personal relief
                  <br />• Consider maximizing before year-end
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography
                  variant="subtitle2"
                  fontWeight={600}
                  gutterBottom
                  color="primary"
                >
                  🎁 Tax-Free Benefits
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  • Small Benefit Exemption: €1,000/year in vouchers
                  <br />
                  • Travel & subsistence (within limits)
                  <br />
                  • Mobile phone for business use
                  <br />• Professional subscriptions
                </Typography>
              </Grid>
            </Grid>
          </Box>
        </Collapse>
      </Paper>

      {/* Year-End Checklist */}
      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, height: "100%" }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              📋 Year-End Checklist
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Key tasks to complete before your financial year-end
            </Typography>
            {checklistItems.map((item, index) => (
              <ChecklistItem key={index} {...item} />
            ))}
          </Paper>
        </Grid>

        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, borderRadius: 3, height: "100%" }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              📅 Key Deadlines
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={2}>
              Important dates for Irish company directors
            </Typography>

            <Stack spacing={2}>
              <Alert severity="info">
                <AlertTitle>Preliminary Corporation Tax</AlertTitle>
                Due 23rd of month 6 after your accounting year-end. Pay at least
                90% of your estimated liability.
              </Alert>

              <Alert severity="warning">
                <AlertTitle>Corporation Tax Return (CT1)</AlertTitle>
                Due 23rd of month 9 after your accounting year-end. File via
                ROS.
              </Alert>

              <Alert severity="info">
                <AlertTitle>Annual Return (CRO)</AlertTitle>
                Due within 56 days of your Annual Return Date. File via CORE.
              </Alert>

              <Button
                variant="outlined"
                startIcon={<OpenInNewIcon />}
                href="https://www.ros.ie"
                target="_blank"
                rel="noopener noreferrer"
                fullWidth
              >
                Go to Revenue Online Service (ROS)
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}
